import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, TrendingUp, Gift, ArrowRight } from 'lucide-react';
import ArbitrageExample from './ArbitrageExample';

function HowItWorks() {
  return (
    <div style={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: '80px 60px' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '70px' }}>
          <h1 style={{ fontSize: '42px', fontWeight: '800', color: '#ffffff', margin: '0 0 16px 0' }}>
            How Advantage Betting Works
          </h1>
          <p style={{ fontSize: '17px', color: '#64748b', lineHeight: '1.7', maxWidth: '620px', margin: '0 auto' }}>
            Sportsbooks don't always agree on the odds. When they don't, we find bets that are mathematically in your favor.
          </p>
        </div>

        {/* Arbitrage */}
        <div style={{ marginBottom: '70px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: '10px',
              background: 'linear-gradient(135deg, rgba(0,217,255,0.2), rgba(16,185,129,0.2))',
              border: '1px solid rgba(0,217,255,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Zap size={20} color="#00d9ff" />
            </div>
            <h2 style={{ fontSize: '26px', fontWeight: '700', color: '#ffffff', margin: 0 }}>Arbitrage Betting</h2>
          </div>
          <p style={{ fontSize: '16px', color: '#94a3b8', lineHeight: '1.7', marginBottom: '16px' }}>
            Sportsbooks sometimes post odds that don't add up with each other for a variety of reasons.
            The simplest way to spot a two-way arbitrage: if the positive odds on one side are
            larger in absolute value than the negative odds on the other, that's an arbitrage opportunity.
            For example, Team A at <strong style={{ color: '#ffffff' }}>+150</strong> on
            FanDuel and Team B at <strong style={{ color: '#ffffff' }}>-140</strong> on DraftKings. Since
            150 &gt; 140, that's an arbitrage opportunity.
          </p>
          <p style={{ fontSize: '16px', color: '#94a3b8', lineHeight: '1.7', marginBottom: '28px' }}>
            After finding an arbitrage opportunity, the next challenge is determining optimal bet sizes.
            When balanced correctly, both outcomes return a profit.
          </p>
          <ArbitrageExample />
          {/* <p style={{ fontSize: '14px', color: '#64748b', marginTop: '16px', lineHeight: '1.6' }}>
            Upperline Bet scans 18+ sportsbooks in real time and surfaces opportunities the moment
            they appear, These opportunities may only last for a few minutes before the books change their lines.
          </p> */}
        </div>

        {/* Positive EV */}
        <div style={{ marginBottom: '70px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: '10px',
              background: 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(0,217,255,0.2))',
              border: '1px solid rgba(16,185,129,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <TrendingUp size={20} color="#10b981" />
            </div>
            <h2 style={{ fontSize: '26px', fontWeight: '700', color: '#ffffff', margin: 0 }}>Positive EV Betting</h2>
          </div>
          <p style={{ fontSize: '16px', color: '#94a3b8', lineHeight: '1.7', marginBottom: '16px' }}>
            Think about a fair coin flip. The true probability is 50% each way, so the fair payout is 
            exactly +100. That means a fair bet is bet $100, to win $100. Most sportsbooks would price 
            both sides of a coin flip at -110, which is how they build their margin.
          </p>
          <p style={{ fontSize: '16px', color: '#94a3b8', lineHeight: '1.7', marginBottom: '16px' }}>
            Now imagine someone on the street offers you a different deal: heads, they pay you $200; 
            tails, you pay them $100. You'd take that bet without hesitation. The math overwhelmingly 
            favors you. That's positive EV.
          </p>
          <p style={{ fontSize: '16px', color: '#94a3b8', lineHeight: '1.7', marginBottom: '16px' }}>
            Now, how much would you bet? Not everything you have. Even with a massive edge, you can 
            lose one flip, five in a row, or more. Bet too big and a bad run wipes you out before the edge 
            has time to play out. Bet too small and you're leaving money on the table.
          </p>
          <p style={{ fontSize: '16px', color: '#94a3b8', lineHeight: '1.7', marginBottom: '20px' }}>
            That balance is bankroll management, and the math behind it is the Kelly Criterion. 
            A formula that tells you exactly what fraction of your bankroll to wager given your edge 
            and the odds.{' '}
            <Link to="/positive-ev-calculator" style={{ color: '#00d9ff', textDecoration: 'none', fontWeight: '600' }}>
              Our Kelly Criterion calculator →
            </Link>
          </p>
          <p style={{ fontSize: '16px', color: '#94a3b8', lineHeight: '1.7', marginBottom: '16px' }}>
            In sports betting, edges are never that dramatic. You will not find +200 where the fair line 
            is +100. Identifying lines where the true probability is a few percent in your favor is how 
            profitable betters succeed. Consistent  wins will add up and compound over time.
          </p>
          <div style={{
            padding: '20px 24px',
            backgroundColor: '#1a1f2e',
            border: '1px solid #2d3748',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            flexWrap: 'wrap'
          }}>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Fair Odds</div>
              <div style={{ fontSize: '22px', fontWeight: '700', color: '#ffffff' }}>+100</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>True implied odds</div>
            </div>
            <ArrowRight size={18} color="#2d3748" />
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Soft book offers</div>
              <div style={{ fontSize: '22px', fontWeight: '700', color: '#10b981' }}>+105</div>
              <div style={{ fontSize: '12px', color: '#10b981' }}>+2.5% EV edge</div>
            </div>
            <div style={{ textAlign: 'center', flex: 1, padding: '12px', backgroundColor: 'rgba(16,185,129,0.08)', borderRadius: '8px', border: '1px solid rgba(16,185,129,0.2)' }}>
              <div style={{ fontSize: '12px', color: '#10b981', marginBottom: '4px', fontWeight: '600' }}>Expected Value</div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#10b981' }}>+$2.50</div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>per $100 bet</div>
            </div>
          </div>
        </div>

        {/* Bonus Bet */}
        <div style={{ marginBottom: '70px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: '10px',
              background: 'linear-gradient(135deg, rgba(245,158,11,0.2), rgba(239,68,68,0.2))',
              border: '1px solid rgba(245,158,11,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Gift size={20} color="#f59e0b" />
            </div>
            <h2 style={{ fontSize: '26px', fontWeight: '700', color: '#ffffff', margin: 0 }}>Bonus Bet Conversion</h2>
          </div>
          <p style={{ fontSize: '16px', color: '#94a3b8', lineHeight: '1.7', marginBottom: '16px' }}>
            Sportsbooks hand out bonus bets and free bets constantly. The catch is that you can't withdraw 
            them directly. You have to bet it, win, and then withdraw the winnings.
          </p>
          <p style={{ fontSize: '16px', color: '#94a3b8', lineHeight: '1.7', marginBottom: '16px' }}>
            Most people slap it on a favorite and hope for a small payout, or swing for the fences on a 
            longshot and rarely cash. Either way, there's a good chance that bonus just disappears.
          </p>
          <p style={{ fontSize: '16px', color: '#94a3b8', lineHeight: '1.7', marginBottom: '20px' }}>
            There's a better approach. By pairing your bonus bet with a hedge on the other side, you can 
            <strong style={{ color: '#ffffff' }}> lock in 70–80% of its face value</strong> as real, withdrawable cash. It doesn't matter which side wins. 
            With a $100 bonus bet, you would walk away with $70–80 instead of leaving it up to chance.
            Here's what that looks like:
          </p>
          <div style={{ padding: '20px 24px', backgroundColor: '#1a1f2e', border: '1px solid #2d3748', borderRadius: '12px' }}>
            <div style={{ fontSize: '13px', color: '#f59e0b', fontWeight: '600', marginBottom: '16px' }}>EXAMPLE: $100 Bonus Bet Conversion</div>

            {/* Bets */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div style={{ padding: '12px', backgroundColor: '#0f1419', borderRadius: '8px', border: '1px solid #2d3748' }}>
                <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>Bonus bet (FanDuel)</div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: '#ffffff' }}>Team A +350</div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>$100 bonus → wins $350</div>
              </div>
              <div style={{ padding: '12px', backgroundColor: '#0f1419', borderRadius: '8px', border: '1px solid #2d3748' }}>
                <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>Cash hedge (DraftKings)</div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: '#ffffff' }}>Team B -400</div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>$280 cash → wins $70</div>
              </div>
            </div>

            {/* Outcome table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '14px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #2d3748' }}>
                  <td style={{ padding: '8px 10px', color: '#64748b', fontWeight: '600' }}></td>
                  <td style={{ padding: '8px 10px', color: '#a0aec0', fontWeight: '600', textAlign: 'center' }}>If Team A wins</td>
                  <td style={{ padding: '8px 10px', color: '#a0aec0', fontWeight: '600', textAlign: 'center' }}>If Team B wins</td>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #2d3748' }}>
                  <td style={{ padding: '8px 10px', color: '#64748b' }}>Bonus bet (+350)</td>
                  <td style={{ padding: '8px 10px', color: '#10b981', fontWeight: '600', textAlign: 'center' }}>+$350</td>
                  <td style={{ padding: '8px 10px', color: '#64748b', textAlign: 'center' }}>$0 lost</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #2d3748' }}>
                  <td style={{ padding: '8px 10px', color: '#64748b' }}>Cash hedge (-400)</td>
                  <td style={{ padding: '8px 10px', color: '#ef4444', fontWeight: '600', textAlign: 'center' }}>−$280</td>
                  <td style={{ padding: '8px 10px', color: '#10b981', fontWeight: '600', textAlign: 'center' }}>+$70 (+$280 back)</td>
                </tr>
                <tr>
                  <td style={{ padding: '10px 10px 4px', color: '#ffffff', fontWeight: '700' }}>Net</td>
                  <td style={{ padding: '10px 10px 4px', textAlign: 'center' }}>
                    <span style={{ color: '#f59e0b', fontWeight: '800', fontSize: '15px' }}>+$70</span>
                  </td>
                  <td style={{ padding: '10px 10px 4px', textAlign: 'center' }}>
                    <span style={{ color: '#f59e0b', fontWeight: '800', fontSize: '15px' }}>+$70</span>
                  </td>
                </tr>
              </tbody>
            </table>

            <div style={{ padding: '10px 14px', backgroundColor: 'rgba(245,158,11,0.08)', borderRadius: '8px', border: '1px solid rgba(245,158,11,0.2)', fontSize: '13px', color: '#94a3b8' }}>
              Your $100 bonus bet becomes <strong style={{ color: '#f59e0b' }}> $70 that you can withdraw</strong>, regardless of the outcome.
            </div>
          </div>
        </div>

        {/* CTA */}
        <div style={{ textAlign: 'center', padding: '50px 40px', backgroundColor: '#1a1f2e', border: '1px solid #2d3748', borderRadius: '16px' }}>
          <h3 style={{ fontSize: '28px', fontWeight: '700', color: '#ffffff', margin: '0 0 12px 0' }}>Ready to find real opportunities?</h3>
          <p style={{ fontSize: '15px', color: '#64748b', margin: '0 0 28px 0' }}>We surface arbs, +EV bets, and bonus conversions in real time across 18+ sportsbooks.</p>
          <Link to="/signup">
            <button style={{
              padding: '14px 36px',
              background: 'linear-gradient(135deg, #00d9ff 0%, #10b981 100%)',
              border: 'none', borderRadius: '10px',
              color: '#0a0e1a', fontSize: '15px', fontWeight: '700',
              cursor: 'pointer', boxShadow: '0 8px 24px rgba(0,217,255,0.3)'
            }}>
              Try Free for 7 Days
            </button>
          </Link>
        </div>

      </div>
    </div>
  );
}

export default HowItWorks;
